// Education Network transmission — interactive classified briefing for schools.
import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowRight, ChevronDown, Users, FlaskConical, UserCheck, BookOpenCheck, HandCoins } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Kicker } from "@/components/Section";
import { TransmissionIntro } from "@/components/transmission/TransmissionIntro";
import { EducationCheckpoint } from "@/components/transmission/EducationCheckpoint";
import { ArchitectNote } from "@/components/transmission/ArchitectNote";
import { ClassifiedDossier } from "@/components/transmission/ClassifiedDossier";

const INTRO_LINES = [
  { kicker: "Signal locked", text: "Identity Confirmed" },
  { kicker: "Transmission 01", text: "Education Network Clearance Request" },
];

const ARCHITECT_LINES = [
  "Somewhere inside your classrooms is a photographer who needs a real campaign.",
  "A stylist who needs a set.",
  "A filmmaker who needs a story.",
  "A designer who needs a client.",
  "A student who simply needs someone to open the first door.",
];

const OPTIONS = [
  { icon: Users, title: "Deploy Talent", short: "Refer students or alumni to the cohort.", body: "Nominate emerging creatives from your programs for the Founding Cohort. Career services, faculty, and department referrals give your students a direct line into real, supervised campaign work that ends in portfolio-ready results they fully own." },
  { icon: FlaskConical, title: "Open the Lab", short: "Explore opportunities involving facilities, studios, labs, classrooms, or equipment.", body: "Your studios, labs, salons, kitchens, and classrooms can become mission sites. Facility partnerships let cohort projects happen inside professional-grade spaces — and put your institution's name on the work that comes out of them." },
  { icon: UserCheck, title: "Assign a Mentor", short: "Connect faculty or industry professionals with emerging creatives.", body: "Faculty and industry professionals in your network can guide cohort members through portfolio reviews, workshops, on-set supervision, and career navigation. A single mentor can change the trajectory of a career." },
  { icon: BookOpenCheck, title: "Coordinate Credit", short: "Explore internship, practicum, lab, or experiential learning opportunities where approved by the institution.", body: "Where your institution approves it, cohort participation can align with internship, practicum, lab, or experiential learning requirements. Credit decisions always remain with the school — we build the documentation and structure to support them." },
  { icon: HandCoins, title: "Sponsor a Student", short: "Help cover resources, materials, production expenses, or participation costs.", body: "Sponsorship can cover materials, production expenses, transportation, or participation costs for students who have the talent but not the resources. Every sponsored seat is a first door opened." },
];

export default function TransmissionEducation() {
  const reduced = useReducedMotion();
  const [introDone, setIntroDone] = useState(false);
  const [cleared, setCleared] = useState(() => {
    try { return !!sessionStorage.getItem("blv_edu_clearance"); } catch { return false; }
  });
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <div className="bg-onyx text-cream">
      <TransmissionIntro lines={INTRO_LINES} onDone={() => setIntroDone(true)} testId="edu-intro" />
      {introDone && !cleared && <EducationCheckpoint onDone={() => setCleared(true)} />}
      {introDone && cleared && (
        <div data-testid="transmission-education-page">
          {/* Architect transmission */}
          <section className="grain-dark relative overflow-hidden border-b border-gold/20 bg-onyx py-24 sm:py-32">
            <div className="vignette pointer-events-none absolute inset-0" />
            <div className="halftone-gold pointer-events-none absolute inset-0 opacity-[0.04]" />
            <div className="relative mx-auto max-w-[860px] px-4 text-center sm:px-6">
              <Reveal>
                <p className="font-mono text-[10px] uppercase tracking-[0.5em] text-gold">
                  <span className="mr-2 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-gold align-middle" aria-hidden="true" />
                  Transmission from The Architect
                </p>
              </Reveal>
              <div className="mt-10 space-y-6">
                {ARCHITECT_LINES.map((l, i) => (
                  <motion.p key={l}
                    initial={{ opacity: 0, y: reduced ? 0 : 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6, delay: reduced ? 0 : i * 0.12 }}
                    className="font-cinzel text-base uppercase tracking-[0.15em] text-cream/90 sm:text-xl">
                    {l}
                  </motion.p>
                ))}
              </div>
              <Reveal delay={0.3}>
                <div className="gold-rule mx-auto mt-12 w-56" />
                <h1 className="mt-10 font-display text-4xl uppercase leading-[0.9] tracking-tight text-metallic sm:text-6xl">
                  We have the missions.<br /><span className="text-gold-metallic">You have the talent.</span>
                </h1>
                <p className="mx-auto mt-6 max-w-xl font-body text-lg leading-relaxed text-silver/85">
                  Big Little Village invites educational institutions to connect emerging creatives with collaborative, real-world creative projects.
                </p>
              </Reveal>
            </div>
          </section>

          {/* Options */}
          <section className="grain-dark border-b border-gold/20 bg-charcoal py-20 sm:py-28">
            <div className="mx-auto max-w-[860px] px-4 sm:px-6">
              <Reveal>
                <Kicker>Choose your entry point</Kicker>
                <h2 className="mt-5 font-display text-3xl uppercase leading-[0.95] tracking-tight text-metallic sm:text-5xl">
                  How would your institution like to enter the Village?
                </h2>
              </Reveal>
              <div className="mt-10 space-y-4" data-testid="education-options">
                {OPTIONS.map((o, i) => {
                  const Icon = o.icon;
                  const open = openIdx === i;
                  return (
                    <div key={o.title} className={`border bg-onyx/70 transition-colors duration-300 ${open ? "border-gold/60 shadow-[0_0_30px_rgba(201,162,39,0.1)]" : "border-silver/20 hover:border-gold/40"}`}>
                      <button onClick={() => setOpenIdx(open ? null : i)} aria-expanded={open} data-testid={`edu-option-${i}`}
                        className="flex w-full items-center justify-between gap-4 p-5 text-left sm:p-6">
                        <span className="flex items-center gap-4">
                          <span className={`flex h-11 w-11 shrink-0 items-center justify-center border transition-colors ${open ? "border-gold bg-gradient-to-b from-goldlight to-gold text-onyx" : "border-gold/40 text-gold"}`}>
                            <Icon size={20} strokeWidth={1.6} />
                          </span>
                          <span>
                            <span className="block font-head text-lg font-extrabold uppercase tracking-[0.08em] text-cream sm:text-xl">{o.title}</span>
                            <span className="mt-0.5 block font-body text-sm text-silver/70">{o.short}</span>
                          </span>
                        </span>
                        <ChevronDown size={16} className={`shrink-0 text-gold transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                      </button>
                      <AnimatePresence initial={false}>
                        {open && (
                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: reduced ? 0 : 0.35, ease: [0.2, 0.8, 0.2, 1] }} className="overflow-hidden">
                            <p className="border-t border-gold/15 p-5 font-body text-sm leading-relaxed text-silver/85 sm:p-6">{o.body}</p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Architect note + dossier */}
          <section className="grain-dark border-b border-gold/20 bg-onyx py-16 text-center sm:py-20">
            <div className="mx-auto max-w-[860px] px-4 sm:px-6">
              <ArchitectNote quote="Talent isn't the missing resource. Access is." />
            </div>
          </section>

          {/* Final clearance */}
          <section className="grain-dark relative bg-charcoal py-20 sm:py-28">
            <div className="halftone-gold pointer-events-none absolute inset-0 opacity-[0.04]" />
            <div className="relative mx-auto max-w-[720px] px-4 text-center sm:px-6">
              <Reveal>
                <div className="gold-frame grain-dark relative bg-onyx p-8 sm:p-12">
                  <div className="pointer-events-none absolute inset-2 border border-gold/15" />
                  <p className="font-mono text-[10px] uppercase tracking-[0.45em] text-gold" data-testid="edu-clearance-ready">Clearance request ready</p>
                  <h2 className="mt-5 font-display text-3xl uppercase leading-[0.95] tracking-tight text-metallic sm:text-5xl">
                    Open the first door.
                  </h2>
                  <p className="mx-auto mt-5 max-w-md font-body text-base leading-relaxed text-silver/85">
                    Your organization may have exactly what one of our emerging creatives needs for their next mission.
                  </p>
                  <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                    <Link to="/apply/school" data-testid="request-education-clearance-btn"
                      className="btn-sweep inline-flex items-center gap-2 border border-gold bg-gradient-to-b from-goldlight to-gold px-8 py-3.5 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-onyx transition-transform duration-200 hover:-translate-y-0.5">
                      Request Education Clearance <ArrowRight size={15} />
                    </Link>
                    <Link to="/" className="border border-silver/40 px-8 py-3.5 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-silver transition-colors hover:border-gold hover:text-goldlight">
                      Return to the Village
                    </Link>
                  </div>
                </div>
              </Reveal>
              <div className="mt-14">
                <Reveal><Kicker>Curious about the industry side?</Kicker></Reveal>
                <div className="mt-6"><ClassifiedDossier kind="industry" /></div>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
